import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useComponentI18n } from '@rdrive/i18n';
import messages from './messages/index.js';

// The four vendor events and the element each one reports (moved verbatim from
// project-overview's App/setup.js, 6b-1).
const FULLSCREEN_EVENTS = [
  ['fullscreenchange', 'fullscreenElement'],
  ['webkitfullscreenchange', 'webkitFullscreenElement'],
  ['mozfullscreenchange', 'mozFullscreenElement'],
  ['MSFullscreenChange', 'msFullscreenElement'],
];

export default (props, { emit }) => {
  // LOCAL composer with the warn flags off — see @rdrive/i18n for why the flags are
  // assigned rather than passed.
  const { t } = useComponentI18n(messages);

  // The app bar's active state. `useRoute()` is the same reactive route `$route` exposes; the
  // app must have installed a router (the nav buttons are `:to="{ name }"` links).
  const route = useRoute();

  // --- welcome dialog: the app owns the open state (v-model:showWelcome) ----------------
  const welcomeOpen = computed({
    get: () => props.showWelcome,
    set: (value) => emit('update:showWelcome', value),
  });

  // --- DuckDB query dialog: opened from the kebab, closed by itself ---------------------
  const showDuckDBDialog = ref(false);

  // --- missing-params dialog -------------------------------------------------------------
  // Open while `missingParams` is non-empty, until Close is pressed. `persistent`, so Close
  // is the only way out; the app decides what follows (project-overview: window.close()).
  const missingParamsDismissed = ref(false);
  const showMissingParams = computed({
    get: () => props.missingParams.length > 0 && !missingParamsDismissed.value,
    set: (value) => {
      if (!value) missingParamsDismissed.value = true;
    },
  });
  const closeMissingParams = () => {
    missingParamsDismissed.value = true;
    emit('closeMissingParams');
  };

  // --- fullscreen ------------------------------------------------------------------------
  const isFullscreen = ref(false);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        } else if (document.documentElement.webkitRequestFullscreen) { // Safari
          await document.documentElement.webkitRequestFullscreen();
        } else if (document.documentElement.msRequestFullscreen) { // IE11
          await document.documentElement.msRequestFullscreen();
        }
        isFullscreen.value = true;
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { // Safari
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { // IE11
          await document.msExitFullscreen();
        }
        isFullscreen.value = false;
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  // Named handlers, so onUnmounted really removes them (the app's anonymous copies never were).
  const fullscreenListeners = FULLSCREEN_EVENTS.map(([event, property]) => [
    event,
    () => {
      isFullscreen.value = !!document[property];
    },
  ]);

  onMounted(() => {
    fullscreenListeners.forEach(([event, handler]) => document.addEventListener(event, handler));
  });

  onUnmounted(() => {
    fullscreenListeners.forEach(([event, handler]) => document.removeEventListener(event, handler));
  });

  return {
    // `emit` is a template binding: the kebab's theme switch, the DuckDB export and the
    // welcome dialog's events are forwarded inline.
    emit,
    t,
    route,
    welcomeOpen,
    showDuckDBDialog,
    showMissingParams,
    closeMissingParams,
    isFullscreen,
    toggleFullscreen,
  };
};
